import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import useSignalR from "@entrilia/shared/hooks/useSignalR";
import { getBackendBaseUrl } from "@entrilia/shared/variables";
import { Box } from "@mui/material";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate, useParams } from "react-router";
import { api } from "../../api/client";
import { InboxStateContextProvider } from "../../contexts/InboxStateContext";
import useClientContext from "../../contexts/useClientContext";
import { useInboxState } from "../../hooks/inboxState/inboxState";
import { useQueryParams } from "../../hooks/useQueryParams";
import { createImpersonationHeaders } from "../../impersonation/impersonationHandler";
import { inboxStructureLoadingSelector } from "../../store/state/inboxStructure/selectors";
import { InboxStructureLoadingEnum } from "../../store/state/inboxStructure/types";
import { filterCategoriesSelector, filterLoadingSelector } from "../../store/state/messages/selectors";
import { loadConfiguration } from "../../store/state/messages/thunks";
import { FilterConfigurationLoadingState } from "../../store/state/messages/types";
import {
  clientCodeSelector,
  impersonationSelector,
  loadingIdentitySelector,
  userEmailSelector,
} from "../../store/state/user/selectors";
import type { AppDispatch } from "../../store/store";
import Banner from "../common/Banner";

const INBOX_UPDATE_DEBOUNCE_MS = 1000;

interface Props {
  keepPage?: boolean;
}

const InboxContainer = ({ keepPage }: Props) => {
  const { isDashboardPageAvailable } = useClientContext();
  const { tag, id, page } = useParams();

  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useQueryParams();

  const refresh = (location.state as { refresh: boolean })?.refresh === true;

  const attachmentId = queryParams.get("attachment") ?? undefined;

  const categories = useSelector(filterCategoriesSelector);
  const identityLoading = useSelector(loadingIdentitySelector);
  const filterConfigurationLoading = useSelector(filterLoadingSelector);
  const inboxStructureLoading = useSelector(inboxStructureLoadingSelector);
  const isImpersonationMode = useSelector(impersonationSelector);
  const userEmail = useSelector(userEmailSelector);
  const clientCode = useSelector(clientCodeSelector);

  const pageIndex = keepPage ? Number(page?.slice(1) || "0") : 0;

  const {
    path,
    criteria,
    ui,
    messageId,
    updateCriteria,
    updateCriteriaPreload,
    updateUi,
    setMessageId,
    searchWithCurrentCriteria,
  } = useInboxState(categories || [], tag, id, refresh, pageIndex, attachmentId);

  useEffect(() => {
    dispatch(loadConfiguration());
  }, [dispatch]);

  useEffect(() => {
    if (path) {
      navigate(path, { replace: true });
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [path]);

  useSignalR({
    url: `${getBackendBaseUrl("ir-portal")}/inboxHub`,
    subscriptions: [
      {
        eventName: "inboxUpdated",
        callback: () => searchWithCurrentCriteria(),
        debounceMs: INBOX_UPDATE_DEBOUNCE_MS,
      },
    ],
    customHeaders: Object.fromEntries(createImpersonationHeaders(clientCode, userEmail, "Investor")),
    onConnected: async (connection) => {
      const { data: subscriptionToken } = await api.inboxUpdatesSubscription.getToken();
      if (subscriptionToken) {
        await connection.invoke("Subscribe", subscriptionToken);
      }
    },
    filter: () => isImpersonationMode,
  });

  if (identityLoading) {
    return <></>;
  }

  if (
    filterConfigurationLoading === FilterConfigurationLoadingState.Error ||
    inboxStructureLoading === InboxStructureLoadingEnum.Error
  ) {
    return <DataLoadingFailed />;
  }

  return (
    <InboxStateContextProvider
      value={{
        criteria,
        ui,
        messageId,
        updateCriteria,
        updateCriteriaPreload,
        updateUi,
        setMessageId,
      }}
    >
      {!isDashboardPageAvailable && <Banner />}
      <Box sx={{ display: "flex", width: "100%", flex: 1, minHeight: 0 }}>
        <Outlet />
      </Box>
    </InboxStateContextProvider>
  );
};

export default InboxContainer;
